import type { AppContextEntry } from '../../chat-app-context'
import { formatCount, summarizeHistogram, summarizeList } from '../../chat-app-context'

/** Minimal shape of a loaded graph node this producer needs. */
export interface GraphContextNode {
  /** Neo4j internal id — what the canvas selects on. */
  _id: string
  /** Ontology label. */
  label?: unknown
  /** URN — the agent's tool-call anchor. */
  id?: unknown
}

export interface GraphAppContextInput {
  /** Canvas selection, in click order (internal ids). */
  selectedNodeIds: readonly string[]
  /** Nodes highlighted from a deep link / citation (internal ids). */
  highlightedNodeIds: readonly string[]
  /** Raw search box text. */
  search: string
  /** Number of hits for the last executed search, when one has run. */
  searchResultCount?: number
  /** The graph currently rendered, or null while it loads. */
  graph: { nodes: readonly GraphContextNode[]; relationships: readonly unknown[] } | null
}

/** How many URNs are named verbatim in a selection summary. */
const MAX_NAMED_NODES = 5

function anchorOf(node: GraphContextNode | undefined, fallbackId: string): string {
  const urn = typeof node?.id === 'string' ? node.id : ''
  return urn === '' ? fallbackId : urn
}

/**
 * Graph selection is local component state, so nothing about it reaches the
 * agent through the URL. Selection first: it is the deictic anchor for
 * "explain this", and the URNs are what the agent expands from via Cypher.
 */
export function buildGraphAppContextEntries(input: GraphAppContextInput): AppContextEntry[] {
  const entries: AppContextEntry[] = []
  const nodesById = new Map<string, GraphContextNode>()
  for (const node of input.graph?.nodes ?? []) nodesById.set(node._id, node)

  if (input.selectedNodeIds.length > 0) {
    const labels: string[] = []
    for (const id of input.selectedNodeIds) {
      const label = nodesById.get(id)?.label
      if (typeof label === 'string') labels.push(label)
    }
    const anchors = input.selectedNodeIds
      .slice(0, MAX_NAMED_NODES)
      .map((id) => anchorOf(nodesById.get(id), id))
    entries.push({
      key: 'Selected nodes',
      value: summarizeList(anchors, {
        noun: 'node',
        total: input.selectedNodeIds.length,
        max: MAX_NAMED_NODES,
        detail: summarizeHistogram(labels)
      })
    })
  }

  if (input.highlightedNodeIds.length > 0) {
    entries.push({
      key: 'Highlighted',
      value: summarizeList(
        input.highlightedNodeIds
          .slice(0, MAX_NAMED_NODES)
          .map((id) => anchorOf(nodesById.get(id), id)),
        { noun: 'node', total: input.highlightedNodeIds.length, max: MAX_NAMED_NODES }
      )
    })
  }

  const search = input.search.trim()
  if (search !== '') {
    entries.push({
      key: 'Search',
      value:
        input.searchResultCount === undefined
          ? search
          : `${search} (${formatCount(input.searchResultCount, 'match', 'matches')})`
    })
  }

  if (input.graph !== null) {
    entries.push({
      key: 'Graph size',
      value: `${formatCount(input.graph.nodes.length, 'node')}, ${formatCount(
        input.graph.relationships.length,
        'relationship'
      )}`
    })
  }

  return entries
}
